
import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Loader2 } from 'lucide-react';

interface STLViewerProps {
  url?: string;
}

export const STLViewer = ({ url }: STLViewerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;

    // Scene Setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x09090b); // Zinc 950 (Dark Background)
    
    // Grid Helper - Dark but visible
    const gridHelper = new THREE.GridHelper(100, 20, 0x333333, 0x1a1a1a);
    scene.add(gridHelper);

    // Lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(10, 20, 10);
    scene.add(directionalLight);

    const backLight = new THREE.DirectionalLight(0xffffff, 0.4);
    backLight.position.set(-10, -10, -10);
    scene.add(backLight);

    // Camera
    const camera = new THREE.PerspectiveCamera(50, containerRef.current.clientWidth / containerRef.current.clientHeight, 0.1, 1000);
    camera.position.set(50, 50, 50);

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    containerRef.current.appendChild(renderer.domElement);

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;

    // Load STL or Fallback
    const loadModel = () => {
      if (url && !url.includes('#')) {
        const loader = new STLLoader();
        loader.load(
          url,
          (geometry: any) => {
            // Material - Lighter Gray/White for contrast against dark background
            const material = new THREE.MeshPhongMaterial({ 
              color: 0xe4e4e7, // Zinc 200 (Light Gray)
              specular: 0x111111, 
              shininess: 100 
            });
            const mesh = new THREE.Mesh(geometry, material);
            
            geometry.center();
            
            const box = new THREE.Box3().setFromObject(mesh);
            const size = new THREE.Vector3();
            box.getSize(size);
            const maxDim = Math.max(size.x, size.y, size.z);
            const scale = 30 / maxDim;
            mesh.scale.set(scale, scale, scale);
            
            mesh.rotation.x = -Math.PI / 2;
            scene.add(mesh);
            setLoading(false);
          },
          (xhr: any) => {},
          (err: any) => {
            console.error(err);
            setError(true);
            loadFallback();
          }
        );
      } else {
        loadFallback();
      }
    };

    const loadFallback = () => {
      const geometry = new THREE.TorusKnotGeometry(10, 3, 100, 16);
      const material = new THREE.MeshPhongMaterial({ color: 0xe4e4e7, wireframe: false });
      const mesh = new THREE.Mesh(geometry, material);
      scene.add(mesh);
      setLoading(false);
    };

    loadModel();

    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      if (!containerRef.current) return;
      camera.aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, [url]);

  return (
    <div className="relative w-full h-[400px] bg-zinc-950 rounded-xl overflow-hidden border border-zinc-800 shadow-inner">
      <div ref={containerRef} className="w-full h-full" />
      
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
          <Loader2 className="animate-spin text-zinc-400" size={32} />
        </div>
      )}
      
      <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end pointer-events-none">
         <div className="bg-black/50 backdrop-blur border border-zinc-700 px-3 py-1.5 rounded-lg text-xs text-zinc-300 font-medium pointer-events-auto shadow-sm">
            {error || !url ? 'Demo View' : 'File: ' + url.split('/').pop()}
         </div>
      </div>
    </div>
  );
};
